// Качество данных: обращение к своему разделу ядра через BFF. ТОЛЬКО ЧТЕНИЕ.
//
// Устроено как api/xreports.ts и api/nl.ts: свой БАЗА, своя функция запроса, хуки рядом.
// Общего клиента здесь нет намеренно — api/client.ts обслуживает эмуляцию Bitrix REST
// (POST /api/bx), а у модулей ядра форма ответа другая.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import type { ОтветПроверок, ОтветСтрок } from '../lib/dq'

const БАЗА = '/api/dq'

/** Отказ по правам. Отдельный класс, а не общая ошибка: 403 здесь не поломка, а
 *  нормальный ответ сервера, и выглядеть он должен объяснением, а не строкой «Ошибка». */
export class НетДоступа extends Error {
  constructor(msg = 'forbidden') {
    super(msg)
    this.name = 'НетДоступа'
  }
}

/** Тот же разбор ответа, что в api/xreports.ts и api/nl.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  if (res.status === 403) throw new НетДоступа()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 — неизвестный код проверки. Его надо ОБЪЯСНИТЬ, а не показать пустую таблицу:
    // пустая таблица на экране про качество данных читается как «всё чисто».
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// Прогон всех проверок — это два десятка агрегатов по всем крупным таблицам зеркала.
// Ядро держит ответ в кэше несколько минут и присылает его возраст (`cached_sec`),
// поэтому дёргать ручку чаще бессмысленно: придёт то же самое. staleTime здесь ровно
// затем, чтобы переключение фильтров на экране не ходило в сеть — фильтры считаются
// на клиенте (lib/dq.отобрать).
const ПРОГОН = { staleTime: 5 * 60_000, retry: 1 } as const
const СТРОКИ = { staleTime: 60_000, retry: 1 } as const

export function useКачествоДанных(включён = true) {
  return useQuery<ОтветПроверок>({
    queryKey: ['dq', 'checks'],
    queryFn: () => запрос<ОтветПроверок>('/checks'),
    enabled: включён,
    ...ПРОГОН,
  })
}

/**
 * Ответ `/dq/rows` вместе с полями сужения. В `lib/dq.ОтветСтрок` их нет — файл общий с
 * разделом «Качество данных», который спрашивает всю базу и сужения не знает. Дописывать
 * туда поля ради одного экрана значило бы править чужой файл, поэтому расширение живёт здесь.
 *
 * `page_complete` — САМОЕ ВАЖНОЕ поле ответа для карточки клиента. Страница ядра 50 строк,
 * а находок по проверке бывает 388. Пока страница неполна, «дублей у него нет» — неправда,
 * и ядро говорит об этом двумя способами сразу: флагом и оговоркой `page_is_partial`
 * в `data_notes`. Экран обязан показать оба.
 */
export interface ОтветСтрокСужением extends ОтветСтрок {
  /** Вся выборка поместилась в страницу. false — судить по строкам «такого нет» нельзя. */
  page_complete?: boolean
  /** Чем сузили — словами ядра, не нашими. null/отсутствует — сужения не было. */
  narrowed_by?: Record<string, unknown> | null
}

/** Чем можно сузить проверку. Что именно она понимает — в `check.narrow` её же ответа. */
export interface СужениеПроверки {
  /** id компании Bitrix. Настоящее сужение: на 28.07.2026 у 3262 из 388 дублей
   *  судов остаётся 28. */
  company?: string | number
  /** Номер IMO. Второй способ сузить проверки про суда. */
  imo?: string | number
}

const строкаСужения = (с: СужениеПроверки | undefined) => {
  if (!с) return ''
  const p = new URLSearchParams()
  if (с.company !== undefined && с.company !== '') p.set('company', String(с.company))
  if (с.imo !== undefined && с.imo !== '') p.set('imo', String(с.imo))
  const s = p.toString()
  return s ? `&${s}` : ''
}

/**
 * Примеры записей одной проверки. `code` пустой — не спрашиваем вовсе.
 *
 * `сужение` появилось 28.07.2026 вместе с параметрами `company` и `imo` у ручки. Оно
 * настоящее: сужается и список строк, и точный счётчик `total` — ядро считает их ОДНИМ
 * условием (`data_notes.same_condition`). Это не то же самое, что фильтр на клиенте по
 * загруженной странице, и путать их нельзя.
 *
 * Сводом (`/dq/checks`) сузить нельзя вовсе — ядро отвечает на `?company=` 400 с
 * объяснением. Здесь такого вызова нет и быть не должно.
 */
export function useСтрокиПроверки(
  code: string, start = 0, включён = true, сужение?: СужениеПроверки,
) {
  const хвост = строкаСужения(сужение)
  return useQuery<ОтветСтрокСужением>({
    queryKey: ['dq', 'rows', code, start, сужение ?? null],
    queryFn: () => запрос<ОтветСтрокСужением>(
      `/rows?code=${encodeURIComponent(code)}${start ? `&start=${start}` : ''}${хвост}`),
    enabled: включён && !!code,
    ...СТРОКИ,
  })
}
